/**
 * 运行时状态的接口（S7）。两个 routeId，与 `apps/visual-game-server/src/routes/llm-admin/runtime.js`
 * 的 `@callchain.routeId` 标注逐字对应。
 *
 * 返回形状（后端实测，2026-07-13 打 `GET /api/admin/visual-game/llm/runtime/keys` 得到）：
 *
 *   { items: KeyRuntimeRow[], audit_queue: { queued, queued_bytes, overflow, retry_count } }
 *
 * **`items` 只有 enabled 的 key**（keyrouter 的 `stats()` 只吐 enabled）——一把 key 被停用之后，
 * 它就从这份快照里消失，这本身就是「停用生效了」的证据。
 *
 * 字段的口径分两类，**混在一起展示就是谎**：
 *   - `in_flight`（正在占用并发槽的请求数）/ `cooldown_remaining_s` / `tpm` / `rpm` 读 Redis，
 *     是**全局真相**（所有副本共见）。
 *   - `usage` 是 keyrouter 的**进程内存 Map**（`packages/keyrouter/src/router.js:71`）：重启归零，
 *     多副本时只反映当前连上的这一个。它自带 `scope: 'process'` 标记，界面必须原样说出来。
 *
 * @typedef {{ key_id: string, vendor: string, label: string|null, api_key_masked: string,
 *   priority: number, in_flight: number, max_concurrency: number, cooldown_remaining_s: number,
 *   tpm: number, rpm: number, max_tpm: number|null, max_rpm: number|null,
 *   tpm_saturation: number, rpm_saturation: number, auth_failures_24h: number,
 *   usage: { scope: 'process', totals: Record<string, number> } }} KeyRuntimeRow
 * @typedef {{ queued: number, queued_bytes: number, overflow: number, retry_count: number }} AuditQueue
 * @typedef {{ items: KeyRuntimeRow[], audit_queue: AuditQueue }} KeyRuntimeSnapshot
 */

import { llmHttp } from './http.js'

/**
 * key 池此刻的状态。**刷新不要低于 5 秒**：后端的 `stats()` 对每把 key 逐个 await（并发槽 + 两个
 * 分钟窗口），是 2N 次串行 Redis 往返——RTT 1ms 的远端 Redis 上 50 把 key 就是 ~100ms。
 * @callchain.routeId llm.admin.runtime.keys
 * @returns {Promise<KeyRuntimeSnapshot>}
 */
export function listKeyRuntime() {
  return llmHttp().get('/runtime/keys', { routeId: 'llm.admin.runtime.keys' })
}

/**
 * 手动把一把被冷却的 key 放出来：删掉 Redis 上的 `keyrouter:cooldown:<keyId>`，下一次 acquire
 * 立刻会重新把它选进来。
 * @callchain.routeId llm.admin.keys.cooldown.clear
 * @param {string} keyId
 * @returns {Promise<{ key_id: string, cooldown_cleared: true }>}
 */
export function clearKeyCooldown(keyId) {
  return llmHttp().post(`/keys/${encodeURIComponent(keyId)}/clear-cooldown`, {
    routeId: 'llm.admin.keys.cooldown.clear',
  })
}
